import GoogleAnalytics from 'react-ga';
import moment from 'moment';
import _ from 'lodash';
import { notifyNewCallerInWaitingArea } from 'businessLogic/notification';
import { getWaitingAreasDetails } from 'businessLogic/pubnubHelpers';
import {
  getWaitingArea,
  updateWaitingAreasPermission,
  waitingareasNeedToUpdate,
  getParticipantsRoomsInWA,
  getCallSummaryByWAId,
  getConsultNowInitiatedCalls,
  getConsultNowStatus,
  setVidyoRoomIdForCN,
} from 'actions/waitingArea';
import {
  getAllMeetingRooms,
  updateMeetingRoomsPermission,
  getMeetingRoomWithSearchTerm,
  getHomePanelMeetingRooms,
} from 'actions/meetingRoom';
import {
  getWaitingAreaUsers,
  getUserBasicProfile,
  getOrganisationalUnitUsers,
  getMeetingRoomUsers,
} from 'actions/user';
import { getOrgUnitWithSearchTerm, getAllOrgUnits } from 'actions/organisation';
import { updatePubNubClientStatus, setPubnubHeartbeatReceived } from 'actions/networkHealthStatus';
import { queueESEvents, ESEventTypes } from 'businessLogic/logEventToElasticSearch';
import { logout } from 'businessLogic/authorizator';
import { NetworkTypes } from '../../constants/networkTypes.js';

export const PUBNUB_CHANNELS = {
  HEARTBEAT: 'vcc-pubnub-heartbeat',
  WAITING_AREA: 'wa-',
  USER: 'u-',
  MEETING_ROOM: 'mr-',
  ORGANISATIONAL_UNIT: 'org-',
  CONSULT_NOW: 'cn-',
};

export const PUBNUB_TRIGGERS = {
  WAITING_AREA_UPDATED: 'wa-updated',
  WAITING_AREA_NEW_CALLER_JOINED: 'wa-new-caller-joined',
  WAITING_AREA_MEMBER_LIST_UPDATED: 'wa-members-list-updated',
  USER_UPDATED: 'user-updated',
  USER_ORGANISATIONS_UPDATED: 'user-orgsUpdated',
  USER_WAITING_AREAS_NEED_TO_UPDATE: 'user-waitingAreasNeedToUpdate',
  USER_REQUESTED_HEARTBEAT: 'user-requested-heartbeat',
  MEETING_ROOM_PARTICIPANTS_UPDATED: 'mr-participants-updated',
  MEETING_ROOM_UPDATED: 'meetingroom-updated',
  MEETING_ROOM_MEMBER_LIST_UPDATED: 'mr-members-list-updated',
  ORGANISATIONAL_UNIT_UPDATED: 'org-updated',
  ORGANISATIONAL_UNIT_MEMBER_LIST_UPDATED: 'org-members-list-updated',
  CONSULT_NOW_UPDATED: 'consult-now-updated',
};

export const PUBNUB_ACTIONS = {
  METADATA_UPDATED: 'MetaDataChanged',
  CALLER_JOINED: 'CallerJoined',
  CALLER_LEFT: 'CallerLeft',
  CONSULT_NOW_UPDATED: 'consult-now-updated',
  CONSULT_NOW_CANCELED: 'cancelled',
};

export function registerPubnub(subscribeKey, dispatcher) {
  if (window.pubnub) {
    console.log('already registered pubnub');
    return;
  }

  // connect to pubnub
  window.pubnub = new window.PubNub({
    subscribeKey: subscribeKey,
  });

  // channels Pubnub sdk thinks we are connected to
  window.pubnubConnectedChannelsAccordingToPubnub = [];

  // add listener for pubnub message
  addPubnubListener(dispatcher);
}

export function disposePubNub() {
  try {
    unsubscribeAllPubnub();
    window.localStorage.removeItem('subscribeKey');
    window.pubnub.removeAllListeners();
    window.pubnub = undefined;
  } catch (ex) {
    //Log here to Elastic Search
  }
}

function subscribeMaximumPubnubChannels(channelsToConnect, pubnubMaxChannelsToConnect) {
  let maxChannelsToSubscribe =
    window.getState().usersReducer.userProfile.pubnubMaxChannelsToConnect || pubnubMaxChannelsToConnect;
  if (maxChannelsToSubscribe > 0 && channelsToConnect.length > maxChannelsToSubscribe) {
    for (let index = 0; index < channelsToConnect.length; ) {
      let endIndex = index + maxChannelsToSubscribe;
      let channelsToConnectChunk =
        endIndex > channelsToConnect.length ? channelsToConnect.slice(index) : channelsToConnect.slice(index, endIndex);
      window.pubnub.subscribe({
        channels: channelsToConnectChunk,
      });
      index = index + maxChannelsToSubscribe;
    }
  } else {
    window.pubnub.subscribe({
      channels: channelsToConnect,
    });
  }
}

// Subscribe to list of channels
export function subscribePubnubChannels(channels, pubnubMaxChannelsToConnect) {
  console.log('subscribePubnubChannels requested', channels);

  if (!window.pubnubConnectedChannelsAccordingToPubnub) {
    console.log('pubnub seems does not registered yet, this happens in unit-test:', channels);
    return;
  }

  // connected if not already subscribed
  let channelsToConnect = [];
  channels.forEach((channel, index) => {
    if (!window.pubnubConnectedChannelsAccordingToPubnub.includes(channel)) {
      channelsToConnect.push(channel);
    }
  });

  if (channelsToConnect.length > 0) {
    // call pubnub to subscribe
    console.log('subscribing to pubnub channels', channelsToConnect);
    subscribeMaximumPubnubChannels(channelsToConnect, pubnubMaxChannelsToConnect);
  } else {
    console.log('already subscribed to requested channels:', channels);
  }
}
export function subscribeForPubNubUpdates(data, dispatcher, calledByPubNubNotification = false) {
  //This is called from basic info once we have the response from server. If we have asked to fetch basic info against a pubnub request, we do some extra actions.
  if (data) {
    let channels = getAllVCMChannels(data); //it will join all the channels and return them

    let newlyAddedChannels = getChannelsToSubscribe(channels); //see which channels going to add as new
    let removedChannels = getChannelsToUnSubscribe(channels); //see which channels are to be removed

    if (!window.pubnub) {
      registerPubnub(window.localStorage.getItem('subscribeKey'), dispatcher);
    }

    //TODO: we can log the newly and removed channels to ES

    if (newlyAddedChannels.length > 0) {
      subscribePubnubChannels(newlyAddedChannels, data.pubnubMaxChannelsToConnect);
    }

    if (removedChannels.length > 0) {
      unSubscribPubnubChannels(removedChannels);
    }

    ///Now we are doing actions if the fetch basic info was called against a pubnub message. we will need to load other entities as well
    //based upon which entities channel are added or removed

    //if organisation new channels are added or org channel is removed we will need to load org, meeting room and WA as well.

    //if meetingRoom new channels are added or meeting Room channel is removed we will need to load only Meeting Room.
    //if Waiting Area new channels are added or WA channel is removed we will need to load  WAs only.

    var loadWA = false,
      loadMeetingRoom = false,
      loadOrgs = false;

    if (calledByPubNubNotification) {
      //we want to see which entities need to be reloaded.
      if (isWaitingListChange(newlyAddedChannels, removedChannels)) {
        queueESEvents(
          ESEventTypes.CallingWADetails,
          `When subscribeForPubNubUpdates`,
          'eventType&eventValue',
          null,
          null,
          true,
          'info'
        );
        getWaitingAreasDetails(dispatcher, () => {
          loadWA = true;
        });
      }
      if (isMeetingRoomListChange(newlyAddedChannels, removedChannels)) {
        dispatcher(getAllMeetingRooms());
        loadMeetingRoom = true;
      }
      if (isOrganisationListChange(newlyAddedChannels, removedChannels)) {
        dispatcher(getAllOrgUnits());
        loadOrgs = true;
      }
    }
  }
}

function isWaitingListChange(newlyAddedChannels, removedChannels) {
  //we detect here whether we should refresh WA's or not
  return (
    _.some(newlyAddedChannels, _.method('match', PUBNUB_CHANNELS.WAITING_AREA)) ||
    _.some(removedChannels, _.method('match', PUBNUB_CHANNELS.WAITING_AREA)) ||
    isOrganisationListChange(newlyAddedChannels, removedChannels)
  );
}

function isMeetingRoomListChange(newlyAddedChannels, removedChannels) {
  return (
    _.some(newlyAddedChannels, _.method('match', PUBNUB_CHANNELS.MEETING_ROOM)) ||
    _.some(removedChannels, _.method('match', PUBNUB_CHANNELS.MEETING_ROOM)) ||
    isOrganisationListChange(newlyAddedChannels, removedChannels)
  );
}

function isOrganisationListChange(newlyAddedChannels, removedChannels) {
  return (
    _.some(newlyAddedChannels, _.method('match', PUBNUB_CHANNELS.ORGANISATIONAL_UNIT)) ||
    _.some(removedChannels, _.method('match', PUBNUB_CHANNELS.ORGANISATIONAL_UNIT))
  );
}

export function getAllVCMChannels(userProfile) {
  //Combines all the channels and return them
  //Currently we have following channels:

  //a. User Channel (Single)
  //b. Heart beat channel (Single)
  //c. WA's Channel (list)
  //c. Org's Channel (list)
  //c. MR's Channel (list)
  //   CN Channel (list)

  let vcmChannels = [];
  if (!userProfile) {
    userProfile = window.getState().usersReducer.userProfile;
  }

  if (userProfile && userProfile.email && userProfile.email != '') {
    //safety check added with && userProfile.email && userProfile.email != ''. dont want to have empty channels pushed
    vcmChannels = vcmChannels.concat(userProfile.waitingAreas.map((wa) => wa.pubNubChannel));
    vcmChannels.push(userProfile.pubNubChannel); //User Channel
    vcmChannels = vcmChannels.concat(userProfile.meetingRooms.map((wa) => wa.pubNubChannel));
    vcmChannels = vcmChannels.concat(userProfile.organisations.map((wa) => wa.pubNubChannel));
    if (
      window &&
      window.pubnubConnectedChannelsAccordingToPubnub &&
      window.pubnubConnectedChannelsAccordingToPubnub.length > 0
    ) {
      vcmChannels = vcmChannels.concat(window.pubnubConnectedChannelsAccordingToPubnub.filter((e) => e.match('cn-')));
    }
    vcmChannels.push(PUBNUB_CHANNELS.HEARTBEAT);
  }
  return vcmChannels;
}

// ReSync to any channels we should be connected to but found to be not connected
export function reSubscribeMissingPubnubChannels(skipReconnect) {
  let channelsFoundNotConnected = [];
  let vcmChannels = getAllVCMChannels();
  vcmChannels.forEach((channel, index) => {
    if (!window.pubnubConnectedChannelsAccordingToPubnub.includes(channel)) {
      channelsFoundNotConnected.push(channel);
    }
  });

  let channelsToReconnect = skipReconnect ? [] : channelsFoundNotConnected;

  // log the channels sync status
  const logMessage = `Total=${vcmChannels.length} Missing=${channelsFoundNotConnected.length} Reconnecting=${channelsToReconnect.length}`;
  console.log(`reSubscribeMissingPubnubChannels(${skipReconnect}) - ${logMessage}`);

  if (channelsFoundNotConnected.length > 0) {
    // reconnect to required channels
    if (channelsToReconnect.length > 0) {
      console.log(
        `Re-subscribing ${channelsToReconnect.length} of ${vcmChannels.length} channels `,
        channelsToReconnect
      );
      subscribeMaximumPubnubChannels(channelsToReconnect);
    }

    // track channelsFoundNotConnected to Google Analytics
    // TODO - Log this into New Relic instead (after 3.9.1 is out)
    window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
      GoogleAnalytics.event({
        category: 'VCM',
        action: `reSubscribeMissingPubnubChannels(${skipReconnect})`,
        label: logMessage,
      });

    queueESEvents(
      ESEventTypes.ResubscribeMissingChannels.replace('##skipReconnect##', skipReconnect),
      'FirstMissingChannels: ' + logMessage,
      'eventType'
    );

    console.log();
  } else {
    console.log(`No channel requires reconnection`, channelsToReconnect);
  }

  return channelsToReconnect.length;
}

export function getChannelsToUnSubscribe(channelsAccordingToVCM) {
  //Search which channels to remove and return the list
  //Judges based upon the diff between the channels param and pubnubConnectedChannelsAccordingToPubnub
  let channelsToRemove = [];
  if (window.pubnubConnectedChannelsAccordingToPubnub) {
    channelsToRemove = window.pubnubConnectedChannelsAccordingToPubnub.filter((pubnubChannel) => {
      return !_.includes(channelsAccordingToVCM, pubnubChannel);
    });
  }
  return channelsToRemove;
}

export function getChannelsToSubscribe(channels) {
  //Returns the new channels that are being added
  let newChannels = [];
  if (window.pubnubConnectedChannelsAccordingToPubnub) {
    newChannels = channels.filter((channel) => {
      return !_.includes(window.pubnubConnectedChannelsAccordingToPubnub, channel);
    });
  }
  return newChannels;
}

// Any channels we should not be connected  but found to be connected
export function unSubscribPubnubChannels(channelsToUnsubscribeFrom) {
  if (channelsToUnsubscribeFrom.length > 0) {
    // reconnect to required channels
    console.log(
      `Un-subscribing unSubscribeAdditionalPubnubChannels ${channelsToUnsubscribeFrom.length} of ${window.pubnubConnectedChannelsAccordingToPubnub.length} channels `,
      channelsToUnsubscribeFrom
    );

    //The channels to unsubscribe from window.pubnubConnectedChannelsAccordingToPubnub  will be handled in the pubnub events listener
    window.pubnub.unsubscribe({
      channels: channelsToUnsubscribeFrom,
    });
    // track channelsFoundNotAtVCM to Google Analytics
    // TODO - Log this into New Relic instead (after 3.9.1 is out)
    window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
      GoogleAnalytics.event({
        category: 'VCM',
        action: `unSubscribeAdditionalPubnubChannels`,
        label: 'logMessage',
      });

    queueESEvents(
      ESEventTypes.UnSubscribeAdditionalChannels,
      `Un-subscribing unSubscribeAdditionalPubnubChannels ${channelsToUnsubscribeFrom.length} of ${window.pubnubConnectedChannelsAccordingToPubnub.length} channels `,
      'eventType'
    );
  } else {
    console.log(`No channel requires unsubscribe`, channelsToUnsubscribeFrom);
  }

  return channelsToUnsubscribeFrom.length;
}

// Pubnub notifies of connectivity status, message and presence notifications via the listeners.
function addPubnubListener(dispatcher) {
  window.pubnub.addListener({
    status: function (s) {
      // New Status update is available for pubnub connection
      console.log('Pubnub status changed:', s);

      if (s.error) {
        // pubnub error case
        console.error('pubnub error:', s);

        // TODO - Log this into New Relic instead (after 3.9.1 is out)
        window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
          GoogleAnalytics.event({
            category: 'VCM',
            action: 'Pubnub Status Error - ' + s.error.toString(),
          });

        queueESEvents(s.category, s, 'eventType');

        return;
      }

      // keep track of the channels we are connected to according to pubnub
      // if subscribedChannels is undefiend, use empty list & log exception to GA
      if (!!s.subscribedChannels) {
        window.pubnubConnectedChannelsAccordingToPubnub = s.subscribedChannels;
      }

      // TODO - Log this into New Relic instead (after 3.9.1 is out) - 'Pubnub Status Exception - s.subscribedChannels undefiend' - s.category

      if (s.operation === 'PNUnsubscribeOperation' && s.affectedChannels) {
        //remove the unsubscribe channels from list
        //This is just an additional check, as we also remove the from pubnubConnectedChannelsAccordingToPubnub
        //channels once we unsubscrible or unsubscribe all
        let affectedChannels = s.affectedChannels;
        _.remove(window.pubnubConnectedChannelsAccordingToPubnub, function (channel) {
          return _.includes(affectedChannels, channel);
        });

        queueESEvents(
          ESEventTypes.RemoveUnsubscribeChannels,
          `affectedChannels: ${JSON.stringify(
            s.affectedChannels
          )}, pubnubConnectedChannelsAccordingToPubnub: ${JSON.stringify(s.pubnubConnectedChannelsAccordingToPubnub)}`,
          'eventType'
        );
      }

      // look out for pubnub connection status changes
      // https://www.pubnub.com/docs/web-javascript/status-events
      // https://www.pubnub.com/docs/web-javascript/pubnub-javascript-sdk
      // https://www.pubnub.com/docs/web-javascript/pubnub-network-lifecycle
      switch (s.category) {
        case 'PNNetworkUpCategory':
          console.info('Pubnub Status - PNNetworkUpCategory (SDK detected that network is online)');
          updatePubNubClientStatus(true, dispatcher);
          reSubscribeMissingPubnubChannels(false);

          break;
        case 'PNNetworkDownCategory':
          console.error('Pubnub Status - PNNetworkDownCategory (SDK detected that network is down)');
          updatePubNubClientStatus(false, dispatcher);

          // TODO - Log this into New Relic instead (after 3.9.1 is out)
          window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
            GoogleAnalytics.event({
              category: 'VCM',
              action: 'Pubnub Status Change - ' + s.category,
            });

          queueESEvents(s.category, s, 'eventType');

          break;
        case 'PNNetworkIssuesCategory':
          console.error(
            'Pubnub Status - PNNetworkIssuesCategory (A subscribe event experienced an exception when running)'
          );
          // TODO - Log this into New Relic instead (after 3.9.1 is out)
          window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
            GoogleAnalytics.event({
              category: 'VCM',
              action: 'Pubnub Status Change - ' + s.category,
            });

          queueESEvents(s.category, s, 'eventType');
          break;
        case 'PNReconnectedCategory':
          console.info('Pubnub Status - PNReconnectedCategory (SDK was able to reconnect to pubnub.)');

          // resubscribe all channels again
          reSubscribeMissingPubnubChannels(false);
          break;
        case 'PNConnectedCategory':
          console.info(
            'Pubnub Status - PNConnectedCategory (SDK subscribed with a new mix of channels (fired every time the channel / channel group mix changed))'
          );
          // resubscribe to any channels again if required
          reSubscribeMissingPubnubChannels(false);
          break;
        case 'PNAccessDeniedCategory':
          console.error('Pubnub Status - PNAccessDeniedCategory (PAM permission failure)');
          // TODO - Log this into New Relic instead (after 3.9.1 is out)
          window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
            GoogleAnalytics.event({
              category: 'VCM',
              action: 'Pubnub Status Change - ' + s.category,
            });

          queueESEvents(s.category, s, 'eventType');
          break;
        case 'PNMalformedResponseCategory':
          console.error('Pubnub Status - PNMalformedResponseCategory (JSON parsing crashed)');
          // TODO - Log this into New Relic instead (after 3.9.1 is out)
          window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
            GoogleAnalytics.event({
              category: 'VCM',
              action: 'Pubnub Status Change - ' + s.category,
            });

          queueESEvents(s.category, s, 'eventType');
          break;
        case 'PNBadRequestCategory':
          console.error(
            'Pubnub Status - PNBadRequestCategory (The server responded with bad response because the request is malformed)'
          );
          // TODO - Log this into New Relic instead (after 3.9.1 is out)
          window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
            GoogleAnalytics.event({
              category: 'VCM',
              action: 'Pubnub Status Change - ' + s.category,
            });

          queueESEvents(s.category, s, 'eventType');
          break;
        case 'PNTimeoutCategory':
          console.error('Pubnub Status - PNTimeoutCategory (Failure to establish connection due to timeout)');
          // TODO - Log this into New Relic instead (after 3.9.1 is out)
          window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
            GoogleAnalytics.event({
              category: 'VCM',
              action: 'Pubnub Status Change - ' + s.category,
            });
          queueESEvents(s.category, s, 'eventType');
          break;
        case 'PNRequestMessageCountExceedCategory':
          console.error(
            'Pubnub Status - PNRequestMessageCountExceedCategory (The SDK will announce this error if requestMessageCountThreshold is set and the number of messages arrived from (PubNub in memory cache messages) are greater than this)'
          );
          // TODO - Log this into New Relic instead (after 3.9.1 is out)
          window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
            GoogleAnalytics.event({
              category: 'VCM',
              action: 'Pubnub Status Change - ' + s.category,
            });
          queueESEvents(s.category, s, 'eventType');
          break;
        case 'PNUnknownCategory':
          console.error(
            'Pubnub Status - PNUnknownCategory (This status code is returned when the subscriber get’s a non 200 HTTP response code from the server)'
          );
          // TODO - Log this into New Relic instead (after 3.9.1 is out)
          window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
            GoogleAnalytics.event({
              category: 'VCM',
              action: 'Pubnub Status Change - ' + s.category,
            });
          queueESEvents(s.category, s, 'eventType');
          break;
        default:
          const unknownStatus = `Unknown Pubnub Status - ${s.category} ${s.operation}`;
          console.log(unknownStatus);
          // TODO - Log this into New Relic instead (after 3.9.1 is out)
          if (window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID) {
            GoogleAnalytics.event({
              category: 'VCM',
              action: unknownStatus,
            });
          }

          queueESEvents(s.category, s, 'eventType');
      }
    },
    message: function (data) {
      console.info('Pubnub message received:', data);

      // treat any pubnub message (from VCC) received as a Hearbeat
      const currentEpocTimeSeconds = moment.utc().unix();
      dispatcher(setPubnubHeartbeatReceived(currentEpocTimeSeconds, dispatcher));

      if (data.channel.startsWith(PUBNUB_CHANNELS.HEARTBEAT)) {
        console.info('Pubnub Hearbeat recieved from VCC Server', data.message, currentEpocTimeSeconds);
      } else if (data.channel.startsWith(PUBNUB_CHANNELS.WAITING_AREA) && data.message.trigger) {
        // Waiting Area related pubnub event raised, now find the action required
        if (data.message.trigger === PUBNUB_TRIGGERS.WAITING_AREA_UPDATED) {
          let waitingAreaId = data.message.data;
          let networkTypeId;
          if (!isNaN(data.message.networkTypeId)) {
            networkTypeId = parseInt(data.message.networkTypeId);
          }
          if (isNaN(waitingAreaId)) {
            console.error('cannot parse the networkId for channel:', data.message);
          } else {
            queueESEvents(
              ESEventTypes.GetPubnubUpdates,
              `${data.message.trigger} - ${data.message.action} with ${waitingAreaId}`,
              'eventValue',
              null,
              null,
              true,
              'info'
            );
            if (
              data.message.action === PUBNUB_ACTIONS.CALLER_JOINED ||
              data.message.action === PUBNUB_ACTIONS.CALLER_LEFT
            ) {
              // If action is callerJoin
              handleCallerJoinOrLeave(waitingAreaId, dispatcher, networkTypeId);
            } else if (data.message.action === PUBNUB_ACTIONS.METADATA_UPDATED) {
              // If action is MetaDataChanged
              dispatcher(getWaitingArea(parseInt(waitingAreaId)));
            }
          }
        } else if (data.message.trigger === PUBNUB_TRIGGERS.WAITING_AREA_NEW_CALLER_JOINED) {
          const userProfile = window.getState().usersReducer.userProfile;
          let isWaitingAreaAssociatedWithUser = true;
          let networkTypeId;
          if (data.message.data) {
            waitingAreaId = data.message.data;
          } else {
            waitingAreaId = data.message.networkId;
            if (!isNaN(data.message.networkTypeId)) {
              networkTypeId = parseInt(data.message.networkTypeId);
            }
          }

          if (userProfile.shouldLoadDirectlyAssociatedWAs) {
            const waitingAreaIDs = userProfile.waitingAreasDirectlyAssociated.map((waitingaArea) => waitingaArea.id);
            isWaitingAreaAssociatedWithUser = waitingAreaIDs.includes(waitingAreaId);
          }
          queueESEvents(ESEventTypes.GetPubnubUpdates, data.message.trigger, 'eventValue', null, null, true, 'info');
          if (isWaitingAreaAssociatedWithUser) {
            // Don't notify if caller joining group consultation
            if (networkTypeId !== NetworkTypes.GroupConsultation) {
              notifyNewCallerInWaitingArea(data.message);
            }
          }
        } else if (data.message.trigger === PUBNUB_TRIGGERS.WAITING_AREA_MEMBER_LIST_UPDATED) {
          // fetch members again for this waiting area
          var waitingAreaId = data.message.data;
          let locationOfPage = window.location.href.split('?')[0] || window.location.href;

          if (!isNaN(waitingAreaId)) {
            queueESEvents(
              ESEventTypes.GetPubnubUpdates,
              `${data.message.trigger} with ${waitingAreaId}`,
              'eventValue',
              null,
              null,
              true,
              'info'
            );
            // Make Waiting Area Users fetch only for Service Admin and Org Admin who have right to see user details
            if (locationOfPage.indexOf(`/waiting-area/manage-users/${waitingAreaId}`) > -1) {
              dispatcher(getWaitingAreaUsers(parseInt(waitingAreaId)));
            }
          } else {
            console.error('cannot parse the networkId for channel:', data.message);
          }
        } else if (data.message.trigger.toLowerCase() === PUBNUB_TRIGGERS.CONSULT_NOW_UPDATED) {
          var waitingAreaId = data.message.WAId;
          let locationOfPage = window.location.href.split('?')[0] || window.location.href;
          if (locationOfPage.indexOf('waiting-area/view-one') > -1) {
            let parseURLArray = locationOfPage.split('waiting-area/view-one/');
            if (parseURLArray[parseURLArray.length - 1] === waitingAreaId.toString()) {
              // if on PIWA, and wa-id is the same as the data, call the rooms
              queueESEvents(
                ESEventTypes.GetPubnubUpdates,
                `${data.message.trigger} with ${waitingAreaId}`,
                'eventValue',
                null,
                null,
                true,
                'info'
              );
              dispatcher(getConsultNowInitiatedCalls(parseInt(waitingAreaId)));
            }
          }
        }
      } else if (data.channel.startsWith(PUBNUB_CHANNELS.USER) && data.message.trigger) {
        // User releated pubnub event raised, now find the action required

        if (data.message.trigger === PUBNUB_TRIGGERS.USER_UPDATED) {
          var personId = data.message.personId;
          queueESEvents(
            ESEventTypes.GetPubnubUpdates,
            `${data.message.trigger} with ${personId}`,
            'eventValue',
            null,
            null,
            true,
            'info'
          );

          if (personId === window.getState().usersReducer.userProfile.id) {
            let cb = (userBasicProfile) => {
              if (
                userBasicProfile &&
                !userBasicProfile.hasWaitingAreas &&
                !userBasicProfile.hasMeetingRooms &&
                !userBasicProfile.hasOrganisations &&
                !userBasicProfile.hasReports
              ) {
                logout(dispatcher);
              }
            };
            dispatcher(getUserBasicProfile(cb, true));
          } else {
            console.error('cannot parse the personId for channel:', data.message);
          }
        } else if (data.message.trigger === PUBNUB_TRIGGERS.USER_ORGANISATIONS_UPDATED) {
          var personId = data.message.personId;
          queueESEvents(
            ESEventTypes.GetPubnubUpdates,
            `${data.message.trigger} with ${personId}`,
            'eventValue',
            null,
            null,
            true,
            'info'
          );

          if (personId === window.getState().usersReducer.userProfile.id) {
            dispatcher(getAllOrgUnits());
          } else {
            console.error('cannot parse the personId for channel:', data.message);
          }
        } else if (data.message.trigger === PUBNUB_TRIGGERS.USER_WAITING_AREAS_NEED_TO_UPDATE) {
          var personId = data.message.personId;
          queueESEvents(
            ESEventTypes.GetPubnubUpdates,
            `${data.message.trigger} with ${personId}`,
            'eventValue',
            null,
            null,
            true,
            'info'
          );

          if (personId === window.getState().usersReducer.userProfile.id) {
            // It will trigger lazy load for waiting areas when users access for WAs listing page
            dispatcher(waitingareasNeedToUpdate(false));
          } else {
            console.error('cannot parse the personId for channel:', data.message);
          }
        } else if (data.message.trigger === PUBNUB_TRIGGERS.USER_REQUESTED_HEARTBEAT) {
          queueESEvents(ESEventTypes.GetPubnubUpdates, data.message.trigger, 'eventValue', null, null, true, 'info');
          console.info('User Requested Pubnub Hearbeat recieved from VCC Server', data.message);
        }
      } else if (data.channel.startsWith(PUBNUB_CHANNELS.MEETING_ROOM) && data.message.trigger) {
        // Meeting rooms releated pubnub event raised, now find the action required
        if (data.message.trigger === PUBNUB_TRIGGERS.MEETING_ROOM_PARTICIPANTS_UPDATED) {
          var meetingRoomId = data.message.data;
          if (!isNaN(meetingRoomId)) {
            queueESEvents(
              ESEventTypes.GetPubnubUpdates,
              `${data.message.trigger} with ${meetingRoomId}`,
              'eventValue',
              null,
              null,
              true,
              'info'
            );
            dispatcher(getMeetingRoomWithSearchTerm(meetingRoomId));
            // If it's on quick view then update quick view meeting rooms
            if (!!window.getState().modalReducer.showQuickViewModal) dispatcher(getHomePanelMeetingRooms());
          } else {
            console.error('cannot parse the networkId for channel:', data.message);
          }
        } else if (data.message.trigger === PUBNUB_TRIGGERS.MEETING_ROOM_UPDATED) {
          var meetingRoomId = data.message.data;
          if (!isNaN(meetingRoomId)) {
            queueESEvents(
              ESEventTypes.GetPubnubUpdates,
              `${data.message.trigger} with ${meetingRoomId}`,
              'eventValue',
              null,
              null,
              true,
              'info'
            );
            dispatcher(getMeetingRoomWithSearchTerm(meetingRoomId));
          } else {
            console.error('cannot parse the networkId for channel:', data.message);
          }
        } else if (data.message.trigger === PUBNUB_TRIGGERS.MEETING_ROOM_MEMBER_LIST_UPDATED) {
          // fetch members again for this waiting area
          var meetingRoomId = data.message.data;
          let locationOfPage = window.location.href.split('?')[0] || window.location.href;
          if (!isNaN(meetingRoomId)) {
            queueESEvents(
              ESEventTypes.GetPubnubUpdates,
              `${data.message.trigger} with ${meetingRoomId}`,
              'eventValue',
              null,
              null,
              true,
              'info'
            );
            if (locationOfPage.indexOf(`/meeting-room/manage-users/${meetingRoomId}`) > -1) {
              dispatcher(getMeetingRoomUsers(parseInt(meetingRoomId)));
            }
          } else {
            console.error('cannot parse the networkId for channel:', data.message);
          }
        }
      } else if (data.channel.startsWith(PUBNUB_CHANNELS.ORGANISATIONAL_UNIT) && data.message.trigger) {
        // ORGANISATIONAL releated pubnub event raised, now find the action required
        if (data.message.trigger === PUBNUB_TRIGGERS.ORGANISATIONAL_UNIT_UPDATED) {
          var orgNetworkId = data.message.data;
          queueESEvents(
            ESEventTypes.GetPubnubUpdates,
            `${data.message.trigger} with ${orgNetworkId}`,
            'eventValue',
            null,
            null,
            true,
            'info'
          );

          if (!isNaN(orgNetworkId)) {
            dispatcher(getOrgUnitWithSearchTerm(orgNetworkId));
          } else {
            console.error('cannot parse the networkId for channel:', data.message);
          }
        } else if (data.message.trigger === PUBNUB_TRIGGERS.ORGANISATIONAL_UNIT_MEMBER_LIST_UPDATED) {
          var orgNetworkId = data.message.data;
          let locationOfPage = window.location.href.split('?')[0] || window.location.href;
          if (!isNaN(orgNetworkId)) {
            queueESEvents(
              ESEventTypes.GetPubnubUpdates,
              `${data.message.trigger} with ${orgNetworkId}`,
              'eventValue',
              null,
              null,
              true,
              'info'
            );
            if (locationOfPage.indexOf(`/organisational-unit/manage-users/${orgNetworkId}`) > -1) {
              dispatcher(getOrganisationalUnitUsers(orgNetworkId));
            }
          } else {
            console.error('cannot parse the networkId for channel:', data.message);
          }
        }
      } else if (data.channel.startsWith(PUBNUB_CHANNELS.CONSULT_NOW) && data.message.trigger) {
        if (
          data.message.status == PUBNUB_ACTIONS.CONSULT_NOW_CANCELED &&
          data.message.trigger == PUBNUB_TRIGGERS.CONSULT_NOW_UPDATED
        ) {
          // status: "cancelled"
          // trigger: "consult-now-updated"
          // This Scenario is handled in WA update (we do fetch latest initiated calls on WA channel pubnub updates)
        } else if (
          data.message.trigger.toLowerCase() === PUBNUB_TRIGGERS.CONSULT_NOW_UPDATED &&
          data.message.status === 'caller-joined'
        ) {
          var waitingAreaId = data.message.WAID;
          let locationOfPage = window.location.href.split('?')[0] || window.location.href;
          if (locationOfPage.indexOf('waiting-area/view-one') > -1) {
            let parseURLArray = locationOfPage.split('waiting-area/view-one/');
            if (parseURLArray[parseURLArray.length - 1] === waitingAreaId.toString()) {
              // if on PIWA, and wa-id is the same as the data, call the rooms
              queueESEvents(
                ESEventTypes.GetPubnubUpdates,
                `${data.message.trigger} with ${waitingAreaId}`,
                'eventValue',
                null,
                null,
                true,
                'info'
              );
              dispatcher(getConsultNowInitiatedCalls(parseInt(waitingAreaId)));
              // Set, Update, Reset
              // Consult Now ticket is needed to resolve this AAPLAT-5761 observation
              dispatcher(setVidyoRoomIdForCN(data.message.videoRoomId));
            }
          }
        }
      } else {
        console.warn('unhandled pubnub message:', data);
      }
    },
  });
}

function isCurrentWACheck(waitingAreaId) {
  let locationOfPage = window.location.href.split('?')[0] || window.location.href;
  if (locationOfPage.indexOf('waiting-area/view-one') > -1) {
    let parseURLArray = locationOfPage.split('waiting-area/view-one/');
    if (parseURLArray[parseURLArray.length - 1] === waitingAreaId.toString()) {
      return true;
    }
  }
  return false;
}

// Unsubscribe from all channels and all channel groups
export function unsubscribeAllPubnub() {
  window.pubnub && window.pubnub.unsubscribeAll();
  window.pubnubConnectedChannelsAccordingToPubnub = [];
}

export function subscribeForHeartbeat(dispatcher) {
  subscribePubnubChannels([PUBNUB_CHANNELS.HEARTBEAT]);
}

export function handleCallerJoinOrLeave(waitingAreaId, dispatcher, networkTypeId) {
  var locationOfPage = window.location.href.split('?')[0] || window.location.href;
  if (locationOfPage.indexOf('waiting-area/view-one') > -1) {
    let parseURLArray = locationOfPage.split('waiting-area/view-one/');
    if (parseURLArray[parseURLArray.length - 1] === waitingAreaId.toString()) {
      // if on PIWA, and wa-id is the same as the data, call the rooms
      queueESEvents(
        ESEventTypes.APICall,
        `In handleCallerJoinOrLeave(due to pubnub): ${waitingAreaId}`,
        'eventType&apiURL',
        {
          apiURL: window.APP.CONFIGURATION.VCCAPI + 'waitingareas/' + waitingAreaId + '/vidyoRooms',
          browserURL: window.location.href,
        },
        null,
        true,
        'info'
      );
      dispatcher(getParticipantsRoomsInWA(parseInt(waitingAreaId), null, networkTypeId));
    } else {
      // otherwise, reset waitingAreasNeedToUpdate flag
      dispatcher(waitingareasNeedToUpdate(false));
    }
  } else if (locationOfPage.indexOf('waiting-area/view-all') > -1) {
    // if on waiting areas listing page, call the summary
    const userProfile = window.getState().usersReducer.userProfile;

    if (userProfile.shouldLoadDirectlyAssociatedWAs) {
      const waitingAreaIDs = userProfile.waitingAreasDirectlyAssociated.map((waitingaArea) => waitingaArea.id);
      if (waitingAreaIDs.includes(waitingAreaId)) {
        dispatcher(getCallSummaryByWAId(parseInt(waitingAreaId)));
      }
    } else {
      dispatcher(getCallSummaryByWAId(parseInt(waitingAreaId)));
    }
  } else {
    if (window.getState().modalReducer.showQuickViewModal === true) {
      // if on quick view, get call summary
      dispatcher(getCallSummaryByWAId(parseInt(waitingAreaId)));
    } else {
      // reset waitingAreasNeedToUpdate flag
      dispatcher(waitingareasNeedToUpdate(false));
    }
  }
}
